import { Button, EToastType, toast } from '@bfe/moly';
import { LoadingIcon } from '@bfe/moly-icons';
import { ButtonProps } from '@bfe/moly/dist/es/components/ui/button';
import clsx from 'clsx';
import { isNil } from 'lodash-es';
import { ReactNode } from 'react';
import { useChainList } from '../../store/chainList';
import { WalletModalTab, connectWalletModalStore } from '../../store/dialogs';
import { useLocale } from '../../store/i18nMessage';
import { useCurrentWallet } from '../../store/userWallet';
import { walletIdMap } from '../../utils/constants';
import { thirdPartyWallet } from '../../wallet-hub/Web3WalletHubCenter';
import { ChainTypeList } from '../../wallet-hub/core/types';
import { invoke } from '../../wallet-hub/misc';
import { WalletTypesNumber } from '../../types';
import { bybitExtension } from '../../wallet-hub/hubs/BybitExtensionHub';
import { useAsyncFn } from 'react-use';
import { useMemoizedFn } from '../../hooks';

type UseWalletState = ReturnType<typeof useCurrentWallet>;

// 钱包大前提：浏览器钱包、三方钱包（插件）

type PreCondition = 'third' | 'browser';
export type Web3ConnectButtonProps = {
  // walletIsReady为false时的渲染函数（默认是渲染一个loading 转圈圈），函数参数就是那个loading的dom
  onLoading?: (dom: JSX.Element) => ReactNode;
  // loading 态的className
  loadingClassName?: string;

  // 自定义渲染连接钱包按钮，dom就是ConnectButton组件的连接按钮，适用于你想在按钮前加内容或改布局的情况
  onConnectButton?: (dom: JSX.Element, demandState: DemandStateAndAction, walletState: UseWalletState) => JSX.Element;
  // 连接钱包按钮的 buttonProps
  connectButtonProps?: ButtonProps;

  // 当需要展示切换链按钮时，自定义渲染按钮
  onSwitchChainButton?: (dom: JSX.Element, demandState: DemandStateAndAction, walletState: UseWalletState) => JSX.Element;
  switchChainButtonProps?: ButtonProps;

  // 钱包连接和以及钱包满足业务需求的情况下展示的内容。
  children?: ReactNode | ((state: DemandStateAndAction, walletState: UseWalletState) => ReactNode);

  // 当要触发显示连接弹窗时会调用的函数（即上面的showModal），适用于连 连接弹窗都想要自己实现的情况。
  onShowConnect?: () => Promise<void> | void;

  // 当弹出钱包时展示哪个tab
  connectModalActiveTab?: 'bybit' | 'third';

  //用这个属性名 就是不推荐大家用这个属性来筛选钱包，尽量说服产品弱化浏览器内的钱包；
  // 钱包的大模式：是指需要什么钱包 三方钱包（插件） | 浏览器内的云钱包
  /** @deprecated 大前提模式，云钱包已经没有了，所以这个属性名已经不推荐大家用了，大家尽量说服产品弱化浏览器内的钱包 */
  modeDemandNotSuggested?: PreCondition;
  // @deprecated 同 modeDemandNotSuggested，只是为了保持与之前的接口兼容，免得有人用了ConnectWalletButton 还用了 demand 属性
  demand?: PreCondition;
  // evm钱包的 chainId 需求，即需求钱包应该是什么chainId，不满足的情况下会自动展示切链按钮.
  evmChainIdDemand?: number;
  // 是否需要 Bybit Extension
  requireBybitExtension?: boolean;
  // 需要 Bybit Extension 为指定钱包钱包类型，只能是 云钱包、助记词钱包、私钥钱包，有传这个属性的情况下 requireBybitExtension 为 true
  requireBybitExtWalletType?: WalletTypesNumber;

  /**
   * 钱包app的uuid，用于区分钱包app：例如 bybit、okx、metaMask 等
   * 但是和 requireBybitExtension 冲突，requireBybitExtension 优先级更高
   * requireBybitExtension 为 true时，walletAppUUID = walletIdMap.bybit (com.bybit)
   */
  walletAppUUID?: string;
  // 是否忽略 浏览器钱包的evm chainId 不匹配的情况（意味着用户不需要用浏览器钱包的那个evm兼容 provider)：默认：true，即忽略，默认用户不用。
  ignoreBrowserEvmChainIdUnsatisfied?: boolean;
  // 地址类型, TODO： 添加 BTC 的细分地址类型，以及   connector 上增加细分类型地址、userWallet 返回细分btc 类型的地址，例如 btcTaprootAddress btcSegWitAddress之类
  addressType?: ChainTypeList[];

  // 对bybit extension 的版本需求，默认是 4.1.2 及以上
  requireExtVersion?: string;
};

type DemandStateAndAction = {
  walletIsReady: boolean;
  isConnected: boolean;
  isEvmChainSatisfied: boolean;
  isAddressTypeSatisfied: boolean;
  isWalletAppSatisfied: boolean;
  handleSwitchChain: () => void;
  handleShowConnect: () => void;
};

const addressTypeList = ChainTypeList;

export function Web3ConnectButton({
  loadingClassName,
  onLoading,
  onConnectButton,
  children,
  onShowConnect,
  connectModalActiveTab,
  connectButtonProps,
  evmChainIdDemand,
  ignoreBrowserEvmChainIdUnsatisfied = true,
  addressType,
  switchChainButtonProps,
  onSwitchChainButton,
  walletAppUUID: walletAppUUIDProp,
  requireBybitExtWalletType,
  requireBybitExtension: requireBybitExtensionProp = false,
  requireExtVersion,
}: Web3ConnectButtonProps) {
  const walletState = useCurrentWallet();
  const { walletIsReady, isNone, isBrowserWallet, evmChainId, switchEvmChain, getAddress } = walletState;
  const { t } = useLocale();
  const { getChain } = useChainList();

  const requireBybitExtension = !isNil(requireBybitExtWalletType) ? true : requireBybitExtensionProp;

  const walletAppUUID = requireBybitExtension ? walletIdMap.bybit : walletAppUUIDProp;

  const [handleShowConnectState, handleShowConnect] = useAsyncFn(
    useMemoizedFn(async () => {
      if (onShowConnect) {
        return onShowConnect();
      } else if (!isWalletAppSatisfied) {
        return handleSwitchApp();
      } else {
        return connectWalletModalStore.show({
          activeTab: (connectModalActiveTab as WalletModalTab) || WalletModalTab.bybitWallet,
          bybitExtVersion: requireExtVersion,
        });
      }
    }),
  );

  if (!walletIsReady) {
    const dom = (
      <div className={clsx('flex items-center h-full', loadingClassName)}>
        <LoadingIcon className=" size-[14px] animate-spin text-bds-brand-700-normal" />
      </div>
    );
    return onLoading ? onLoading(dom) : dom;
  }

  const isWalletAppSatisfied = invoke(() => {
    if (requireBybitExtension) {
      if (thirdPartyWallet.isBybitWalletExtension) {
        if (requireBybitExtWalletType !== undefined) {
          return walletState.walletType === requireBybitExtWalletType;
        }
        return true;
      }
      return false;
    }
    if (!walletAppUUID) {
      return true;
    }
    return walletAppUUID === thirdPartyWallet.activeWalletHub?.id;
  });

  const isEvmChainSatisfied = invoke(() => {
    if (isNil(evmChainIdDemand)) {
      // 对chainId 没需求，直接满足
      return true;
    }
    if (ignoreBrowserEvmChainIdUnsatisfied && isBrowserWallet) {
      // 忽略 浏览器钱包的evm chain 又在evm钱包的情况下，也直接满足
      return true;
    }
    //
    return evmChainId !== evmChainIdDemand;
  });
  // 地址类型是否符合需求（即有没有需要的地址)
  const isAddressTypeSatisfied = invoke(() => {
    if (!addressType) {
      return true;
    }
    return addressType.every((type) => {
      if (!addressTypeList.includes(type)) {
        // 传入的不是 现在支持的 地址类型，直接返回，提示切换钱包
        return false;
      } else {
        // 找的到地址，就算支持
        return Boolean(getAddress(type));
      }
    });
  });

  const handleSwitchChain = () => {
    if (!evmChainIdDemand) {
      return;
    }
    return switchEvmChain(evmChainIdDemand);
  };

  const handleSwitchApp = async () => {
    if (requireBybitExtension) {
      if (thirdPartyWallet.isBybitWalletExtension) {
        if (requireBybitExtWalletType) {
          return walletState.switchToBybitExtension(requireBybitExtWalletType);
        }
        return;
      }
      if (await bybitExtension.isAvailable) {
        return walletState.switchToBybitExtension(requireBybitExtWalletType);
      }
      handleShowConnect();
      return;
    }
    if (walletAppUUID) {
      return walletState.switchToWallet(walletAppUUID);
    }
    handleShowConnect();
  };

  const demandState: DemandStateAndAction = {
    walletIsReady,
    isConnected: !isNone,
    isEvmChainSatisfied,
    isAddressTypeSatisfied,
    isWalletAppSatisfied,
    handleSwitchChain,
    handleShowConnect,
  };

  if (typeof children === 'function') {
    return children(demandState, walletState);
  }

  const showConnect = isNone || !isAddressTypeSatisfied || !isWalletAppSatisfied;

  if (showConnect) {
    const buttonDom = (
      <Button
        size="x-small"
        variant="primary"
        {...connectButtonProps}
        loading={handleShowConnectState.loading}
        onClick={(e) => {
          connectButtonProps?.onClick?.(e);
          handleShowConnect();
        }}
      >
        {connectButtonProps?.children ? connectButtonProps?.children : isNone ? t('connect_wallet') : t('switchWallets')}
      </Button>
    );

    return onConnectButton ? onConnectButton(buttonDom, demandState, walletState) : buttonDom;
  }

  if (!isEvmChainSatisfied) {
    const chain = getChain(evmChainIdDemand);
    const dom = (
      <Button
        size="x-small"
        variant="primary"
        {...switchChainButtonProps}
        onClick={(e) => {
          switchChainButtonProps?.onClick?.(e);
          handleSwitchChain();
        }}
      >
        {switchChainButtonProps?.children
          ? switchChainButtonProps?.children
          : t('switch_chain', { chainName: chain?.chainName ?? String(evmChainIdDemand) })}
      </Button>
    );
    return onSwitchChainButton ? onSwitchChainButton(dom, demandState, walletState) : dom;
  }

  return children;
}
